// Calls (office-booking prototype, technical design §6.3): phones ring here. The phone system's
// webhook records the call and matches the caller to customers before anyone answers, so the screen
// pops with who it is: a known customer with their consent and recent jobs, a choice when several
// customers share the number, or a new-customer form for an unknown one. From there, book.
// In Blueprint (calls round, stage 5) the page is drawn from the call components: the calls as CallStrip
// lists (calls/CallsLists.tsx), and an open call as its own CallPage, the call's parts on the left and its
// live transcript beside them (calls/CallLiveTranscript.tsx). The strip under the top bar follows a call
// ringing and answered on a phone (calls/CallsStripHost.tsx). Riverstone keeps today's screen.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { localDateOf, timeText } from '@flowology/kernel';
import { liveStream } from './connection/live.ts';
import { client } from './api.ts';
import { Header, type NavProps } from './Nav.tsx';
import { LoadLine, useLadder } from './shared/Ladder.tsx';
import { NewCustomerForm } from './shared/NewCustomer.tsx';
import './calls.css';
import { phoneText, textablePhone, type BookStart, type Call, type Customer, type JobRow } from './types.ts';
import { openJob } from './job/job-page.ts';
import { CallPayment } from './calls/CallPayment.tsx';
import { CallReasonPart } from './calls/CallReason.tsx';
import { BookingRate, MyRate } from './calls/BookingRate.tsx';
import { callWrite, reviewsCalls } from './calls/reasons-model.ts';
import { MIRROR_LINE, useMirror } from './shared/mirror.ts';
import { CallGlance } from './calls/CallGlance.tsx';
import { campaignWord, lineWord } from './calls/source-model.ts';
import { CallTranscript, RecordingsToMatch } from './calls/CallTranscript.tsx';
import { Callbacks } from './calls/Callbacks.tsx';
import { countLabel, LOOK_BACK, type Callback } from './calls/callbacks-model.ts';
import { longDay } from './records/records-model.ts';
import { openJobsFirst, type Glance } from './calls/glance-model.ts';
import { BookForm, type Booked } from './Book.tsx';
import { plainText } from './shared/plain-text.ts';
import { jobStatusWord } from '@flowology/ui-web/words';
import { useDesign } from './blueprint/design.ts';
import { TalkTabs } from './inbox/TalkTabs.tsx';
import './blueprint/inbox.css';
import { AdClickOf } from './shared/AdClick.tsx';
import { CALL_EVENTS, stillRinging } from './calls/ringing-model.ts';
import { CallPage, Link } from '@flowology/ui-web/fl';
import { CallsLists } from './calls/CallsLists.tsx';
import { daySummary } from './calls/calls-lists-model.ts';
import { CallLiveTranscript } from './calls/CallLiveTranscript.tsx';
import { NOTES_SAVE_MS, NoteDrafts, notesValue } from './calls/notes-draft-model.ts';
import './calls/calls-bp.css';

// a pay link paid, or money recorded, while the customer is on the line (F-40)
const MONEY_EVENTS = /^(payment|invoice)\./;
const CALL_NOTE = 'Booking from a call keeps the call on the job, so its timeline starts here.';
/** Notes typed and not on their call yet, by call: kept while Calls closes and opens again (QA-771). */
const drafts = new NoteDrafts();
/** A call that rang a moment ago can be asked for before it can be read: asked again once, a moment
 * later, before the screen says it couldn't get it (QA-773). */
const callSoon = async (id: string): Promise<Call> => {
  try {
    return await client.call<Call>('calls.get', { id });
  } catch {
    await new Promise((r) => setTimeout(r, 700));
    return client.call<Call>('calls.get', { id });
  }
};

const STATUS: Record<string, { chip: string; label: string }> = {
  ringing: { chip: 'sun', label: 'Now, ringing' },
  in_progress: { chip: 'brand', label: 'On the call' },
  completed: { chip: '', label: 'Call ended' },
  missed: { chip: 'warn', label: 'Missed' },
};
const OUTCOME: Record<string, string> = {
  booked: 'Booked',
  not_booked: 'Not booked',
};
/** A recent call's chip: booked, its reason, or how it went. */
const outcomeWord = (c: Call) =>
  c.outcome === 'booked' ? 'Booked' : c.reason ? c.reason.name : c.outcome ? OUTCOME[c.outcome] : null;

function ConsentChip({ c }: { c: Customer }) {
  const t = textablePhone(c);
  if (c.doNotContact) return <span className="chip say bad">Do not contact</span>;
  return 'method' in t ? (
    <span className="chip say ok">OK to text</span>
  ) : (
    <span className="chip say warn">No texts</span>
  );
}

function CustomerCard({
  c,
  jobs,
  glance,
  payment,
  timeZone,
}: {
  c: Customer;
  jobs: JobRow[];
  glance: { g: Glance | null; owingCents?: number } | null;
  payment?: ReactNode;
  timeZone: string;
}) {
  const loc = c.locations[0];
  const contact = c.contacts[0];
  // Blueprint joins with commas, never a middle dot
  const sep = useDesign() === 'blueprint' ? ', ' : ' · ';
  const phone = contact?.methods.find((m) => m.kind === 'phone');
  return (
    <section className="stone card">
      <h2 className="h3">{c.displayName}</h2>
      <div className="ink2">
        {contact?.name}
        {phone && `${sep}${phoneText(phone.value)}`}
      </div>
      {loc && (
        <div className="ink2">
          {loc.line1}, {loc.city}
          {c.locations.length > 1 &&
            `${sep}+${c.locations.length - 1} more location${c.locations.length > 2 ? 's' : ''}`}
        </div>
      )}
      <div className="tagrow">
        <ConsentChip c={c} />
        <span className="chip say">Customer {c.number}</span>
        {c.kind === 'commercial' && <span className="chip say">Commercial</span>}
      </div>
      {loc?.accessNotes && <p className="ink2">Access: {loc.accessNotes}</p>}
      {glance && <CallGlance g={glance.g} owingCents={glance.owingCents} timeZone={timeZone} />}
      <h3 className="h4">Recent jobs</h3>
      {jobs.length ? (
        <ul className="hist">
          {openJobsFirst(jobs).map((j) => (
            <li key={j.id}>
              {/* the job sheet: its estimates, and Cancel */}
              <button type="button" className="row-btn" onClick={() => openJob(j.id)}>
                <span className="who-col">
                  <b>#{j.number}</b> {plainText(j.summary)}
                </span>
                <span className="chip say">{jobStatusWord(j.status)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No jobs yet.</p>
      )}
      {payment}
    </section>
  );
}

function NewCustomer({ call, onCreated }: { call: Call; onCreated: (customerId: string) => void }) {
  return (
    <section className="stone card">
      <h2 className="h3">No customer has {phoneText(call.from)}</h2>
      <h3 className="h4">New customer</h3>
      <NewCustomerForm
        phone={call.from}
        who="this caller"
        consentNote="Consent (asked on this call; saved with who and when)"
        submitLabel={<>Create customer &amp; book</>}
        link={(customerId, idempotencyKey) =>
          client.call(
            'calls.update',
            { id: call.id, customerId },
            idempotencyKey ? { idempotencyKey } : undefined,
          )
        }
        onDone={onCreated}
      />
    </section>
  );
}

export function CallsScreen({
  nav,
  callId,
  onOpenCall,
  onBoard,
}: {
  nav: NavProps;
  callId: string | null;
  onOpenCall: (id: string | null) => void;
  /** The booking form's way to the board (it offers it only while the company mirrors). */
  onBoard: () => void;
}) {
  const zone = nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const bp = useDesign() === 'blueprint';
  const clockOf = (iso: string) => timeText(iso, zone);
  const [recent, setRecent] = useState<Call[] | null>(null); // null until it's here: never a false "No calls yet"
  // While the company mirrors ServiceTitan, a call's customer and outcome are ServiceTitan's, and so
  // are new customers, bookings and payments: the reason, notes and who took it stay ours (setReason)
  const mirroring = useMirror();
  const [recentFailed, setRecentFailed] = useState(false);
  const [recentTry, setRecentTry] = useState(0);
  const recentPhase = useLadder(recent === null, recentTry, recentFailed);
  const [call, setCall] = useState<Call | null>(null);
  const [callFailed, setCallFailed] = useState(false);
  const [callTry, setCallTry] = useState(0);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  // the customer at a glance: null with no customer (or none to show); g null while it loads
  const [glance, setGlance] = useState<{ key: string; g: Glance | null } | null>(null);
  const glanceFor = useRef('');
  // any call changing (a reason, an outcome, a review) reads the week's rate again (P-0052)
  const [callTick, setCallTick] = useState(0);
  const [notes, setNotes] = useState('');
  const openRef = useRef(callId);
  openRef.current = callId;
  // the pinned "Not booked…" opens the call's reason picker (BL-099)
  const [pickSignal, setPickSignal] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  // booking on the call (BL-101): the Book screen's own form, in place of the call's cards, for the
  // call it was opened on; then what it booked, said on that call
  const [booking, setBooking] = useState<BookStart | null>(null);
  const [justBooked, setJustBooked] = useState<{ callId: string; b: Booked } | null>(null);
  const bookingRef = useRef<HTMLElement>(null);
  const [moneyTick, setMoneyTick] = useState(0);
  // what they owe, on the glance's stat line (BL-099): for those it shows billed to (who may see
  // invoices), read again when a payment or an invoice changes
  const [owing, setOwing] = useState<{ key: string; cents: number } | null>(null);
  // Calls, or the booking rate (BL-003): a CSR's own, everyone's for an owner or manager. The callback
  // queue is no tab of its own: it is always in the queue beside the call (BL-099)
  const [part, setPart] = useState<'calls' | 'rate'>('calls');
  // the callback queue (item 2): read with the screen and again on any call change, for its count
  const [backs, setBacks] = useState<{ calls: Callback[] | null; days: number; failed: boolean }>({
    calls: null,
    days: LOOK_BACK[0],
    failed: false,
  });
  const [backsTry, setBacksTry] = useState(0);
  const held = nav.permissions ?? [];
  const everyone = reviewsCalls(held);
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  /** "2:14 p.m." today, "Oct 15, 2026, 2:14 p.m." before. */
  const whenOf = (iso: string) =>
    localDateOf(new Date(iso), zone) === today
      ? clockOf(iso)
      : `${longDay(localDateOf(new Date(iso), zone))}, ${clockOf(iso)}`;

  // Blueprint's Inbox › Calls is today's: its calls, and how many there were and booked (the day's line)
  const [day, setDay] = useState<{ total: number; booked: number } | null>(null);
  const shown = useRef(false); // Recent calls is on screen: a failed re-read only toasts
  const loadRecent = useCallback(async () => {
    try {
      const r = await client.call<{ calls: Call[]; total: number; booked: number }>(
        'calls.recent',
        bp ? { from: today, to: today, limit: 100 } : { limit: 15 },
      );
      setRecent(r.calls);
      if (bp) setDay({ total: r.total, booked: r.booked });
      setRecentFailed(false);
      shown.current = true;
    } catch (e) {
      if (shown.current) setToast(e instanceof RpcError ? e.message : 'Could not load calls.');
      else setRecentFailed(true);
    }
  }, [bp, today]);

  const loadCall = useCallback(async () => {
    if (!callId) {
      setCall(null);
      setCustomer(null);
      glanceFor.current = '';
      setGlance(null);
      return;
    }
    // The call, its customer and their jobs arrive together, so the screen pop fills in at once (L-sheet).
    const c = await callSoon(callId);
    const open = c.customerId ?? c.openCustomerId;
    const [cust, js] = open
      ? await Promise.all([
          client.call<Customer>('customers.get', { id: open }),
          client.call<{ jobs: JobRow[] }>('jobs.list', { customerId: open, limit: 5 }),
        ])
      : [null, { jobs: [] }];
    // another call opened meanwhile: this answer is no longer the screen's
    if (openRef.current !== callId) return;
    setCall(c);
    // what's typed and not on the call yet stays in the box: a re-read never empties it (QA-771)
    setNotes(drafts.shown(c.id, c.notes));
    setCustomer(cust);
    setJobs(js.jobs);
    // the glance fills in after: the card never waits on it. By the customer customers.get gave (a
    // merged one's kept customer), and only if it's still the one open when it answers.
    const key = cust ? `${cust.id}:${c.locationId ?? ''}` : '';
    glanceFor.current = key;
    if (!cust) setGlance(null);
    else {
      setGlance((was) => (was && was.key === key ? was : { key, g: null }));
      void client
        .call<Glance>('customers.glance', {
          customerId: cust.id,
          ...(c.locationId ? { locationId: c.locationId } : {}),
        })
        .then((g) => glanceFor.current === key && setGlance({ key, g }))
        .catch(() => glanceFor.current === key && setGlance(null));
    }
    setCallFailed(false);
  }, [callId]);

  const owingFor = glance?.g && glance.g.billedCents !== null ? glance.key : '';
  const owingCustomer = glance?.g?.customerId;
  useEffect(() => {
    if (!owingFor || !owingCustomer) return;
    let live = true;
    void client
      .call<{ openCents: number }>('customers.money', { customerId: owingCustomer })
      .then((m) => live && setOwing({ key: owingFor, cents: m.openCents }))
      .catch(() => live && setOwing(null));
    return () => {
      live = false;
    };
  }, [owingFor, owingCustomer, moneyTick]);

  // the form opens at its top, wherever the pinned button was pressed
  useEffect(() => {
    if (booking) bookingRef.current?.scrollIntoView({ block: 'nearest' });
  }, [booking]);

  // A popped call (callId changes) is also re-read into Recent, whichever way it arrived.
  useEffect(() => void loadRecent(), [loadRecent, callId, recentTry]);
  // the queue: with the screen, on any call change, and after an action on it
  useEffect(() => {
    let live = true;
    void client
      .call<{ calls: Callback[] }>('calls.callbacks', { days: backs.days })
      .then((r) => live && setBacks((b) => ({ ...b, calls: r.calls, failed: false })))
      .catch(() => live && setBacks((b) => ({ ...b, failed: true })));
    return () => {
      live = false;
    };
  }, [backs.days, callTick, backsTry]);
  useEffect(() => {
    // a new call starts as loading, never as the last one's failure (QA-773)
    setCallFailed(false);
    void loadCall().catch(() => openRef.current === callId && setCallFailed(true));
  }, [loadCall, callTry, callId]);
  // A call that is asked for but not here yet (or not this one) is loading.
  const callPending = !!callId && call?.id !== callId;
  const callPhase = useLadder(callPending, callTry, callFailed);

  // One stream for the screen's life (QA-252): reopening it on every new call lost the event that
  // caused the change. The handler reads the latest loaders through a ref.
  const loaders = useRef({ loadRecent, loadCall });
  loaders.current = { loadRecent, loadCall };
  useEffect(() => {
    const es = liveStream();
    es.addEventListener('change', (e) => {
      const { type } = JSON.parse((e as MessageEvent<string>).data) as { type: string };
      if (MONEY_EVENTS.test(type)) return setMoneyTick((n) => n + 1);
      if (!CALL_EVENTS.test(type)) return;
      setCallTick((n) => n + 1);
      void loaders.current.loadRecent();
      void loaders.current.loadCall().catch(() => undefined);
    });
    // After a reconnect, catch up on whatever rang meanwhile.
    es.addEventListener('open', () => void loaders.current.loadRecent());
    return () => es.close();
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const update = async (patch: Record<string, unknown>, done?: string) => {
    if (!call) return;
    try {
      setCall(await client.call<Call>(callWrite(mirroring), { id: call.id, ...patch }));
      if (done) setToast(done);
    } catch (e) {
      setToast(e instanceof RpcError ? e.message : 'Could not save.');
    }
  };
  // the notes on the call as typed: a pause saves them, so does leaving the box or the call (QA-771)
  const notesTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const saveNotes = async (id: string) => {
    clearTimeout(notesTimer.current);
    const text = drafts.pending(id);
    if (text === null) return;
    try {
      const r = await client.call<Call>(callWrite(mirroring), { id, notes: notesValue(text) });
      drafts.saved(id, r.notes);
      if (openRef.current === id) setCall(r);
    } catch (e) {
      setToast(`Notes not saved yet: ${e instanceof RpcError ? e.message : 'try again.'}`);
    }
  };
  const saveNotesRef = useRef(saveNotes);
  saveNotesRef.current = saveNotes;
  useEffect(() => {
    if (!callId) return;
    return () => void saveNotesRef.current(callId);
  }, [callId]);
  const typeNotes = (id: string, text: string) => {
    setNotes(text);
    drafts.type(id, text);
    clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(() => void saveNotesRef.current(id), NOTES_SAVE_MS);
  };
  // recording a call is an edit: someone who may only see calls isn't offered it (QA-773)
  const records = held.includes('customers.edit');
  const bar = (w = '') => <span className={`ph-bar ${w}`} aria-hidden="true" />;
  // the week's rate is read again when a call's reason, outcome or review may have changed (P-0052)
  const rateVersion = `${callTick}:${call?.id ?? ''}:${call?.outcome ?? ''}:${call?.reason?.id ?? ''}:${call?.reviewed?.at ?? ''}`;

  const status = call ? (STATUS[call.status] ?? { chip: '', label: call.status }) : null;
  // Blueprint: the call's page settles in as the call open on it is answered (DESIGN.md §9.4)
  const seen = useRef<{ id: string; status: string } | null>(null);
  const [settle, setSettle] = useState<string | null>(null);
  useEffect(() => {
    if (!call) return;
    const was = seen.current;
    if (was && was.id === call.id && was.status === 'ringing' && call.status === 'in_progress')
      setSettle(call.id);
    seen.current = { id: call.id, status: call.status };
  }, [call]);
  const bookingHere = booking && call && booking.callId === call.id ? booking : null;
  const bookedHere = justBooked && call && justBooked.callId === call.id ? justBooked.b : null;
  // the job the call booked, by its number: the customer's recent jobs, else what was just booked
  const callJob = call?.jobId
    ? (jobs.find((j) => j.id === call.jobId)?.number ??
      (bookedHere?.jobId === call.jobId ? bookedHere.jobNumber : null))
    : null;
  const startBooking = (start: BookStart) => {
    setJustBooked(null);
    setBooking(start);
  };
  const who = customer
    ? `${customer.contacts[0]?.name ?? customer.displayName} · ${customer.displayName}`
    : call && call.matches.length > 1
      ? `${call.matches.length} customers have ${phoneText(call.from)}`
      : call
        ? `Unknown caller ${phoneText(call.from)}`
        : '';

  /** One call as a queue row: when, who, its state; the open one marked. */
  const row = (c: Call) =>
    bp ? (
      // Blueprint's call row: who and their number, then when and how it went, a missed one in red
      <li key={c.id}>
        <button className={`row-btn ib-crow${c.id === callId ? ' on' : ''}`} onClick={() => onOpenCall(c.id)}>
          <span className="ib-crow-who">
            <b>{c.matches[0]?.customerName ?? 'Unknown caller'}</b>
            <span className="ib-sub">
              {phoneText(c.from)}
              {c.answeredBy ? ` · Answered by ${c.answeredBy}` : ''}
            </span>
          </span>
          <span className="ib-crow-side">
            <span className="t">{clockOf(c.startedAt)}</span>
            <span
              className={`chip say${!outcomeWord(c) && (c.status === 'missed' || c.status === 'ringing') ? ' bad' : ''}`}
            >
              {outcomeWord(c) ?? STATUS[c.status]?.label ?? c.status}
            </span>
          </span>
        </button>
      </li>
    ) : (
      <li key={c.id}>
        <button className={`row-btn${c.id === callId ? ' on' : ''}`} onClick={() => onOpenCall(c.id)}>
          <span className="t">{clockOf(c.startedAt)}</span>
          <span className="who-col">
            <b>{c.matches[0]?.customerName ?? 'Unknown caller'}</b>
            <span className="ink2">
              {' '}
              · {phoneText(c.from)}
              {c.answeredBy ? ` · Answered by ${c.answeredBy}` : ''}
            </span>
          </span>
          <span className="chip say">{outcomeWord(c) ?? STATUS[c.status]?.label ?? c.status}</span>
        </button>
      </li>
    );
  // ringing now, on top of the queue (BL-099): a call still ringing with nothing said yet, from the last
  // quarter hour (one whose end never came is the recent list's, not a ring)
  const ringing = (recent ?? []).filter((c) => stillRinging(c));
  const recentCard = (
    <section className="stone card">
      <h2 className="h4">Recent calls</h2>
      {bp && (
        <p className="ib-sub">
          Pick a call to see who it is. When the phone rings, the caller shows up before you answer.
        </p>
      )}
      {recent === null ? (
        // L-table: blank rows at a row's height, never "No calls yet" before the list is here
        recentPhase !== 'error' && (
          <ul className={`calls${recentPhase === 'wait' ? ' ldwait' : ''}`} aria-busy="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <li key={i} className="row-ph" aria-hidden="true">
                {bar('t')}
                {bar('w40')}
              </li>
            ))}
          </ul>
        )
      ) : recent.length ? (
        <ul className="calls">{recent.map(row)}</ul>
      ) : (
        <p className="muted">
          No calls yet. When the phone rings, the caller shows up here before you answer.
        </p>
      )}
      {recent === null && (
        <LoadLine phase={recentPhase} what="recent calls" onRetry={() => setRecentTry((n) => n + 1)} />
      )}
    </section>
  );

  const ringingCard = ringing.length > 0 && (
    <section className="stone card" aria-label="Ringing now">
      <h2 className="h4">Ringing now</h2>
      <ul className="calls">{ringing.map(row)}</ul>
    </section>
  );
  const callbacks = (
    <Callbacks
      calls={backs.calls}
      days={backs.days}
      failed={backs.failed}
      whenOf={whenOf}
      onOpen={(id) => onOpenCall(id)}
      onOlder={(days) => setBacks((b) => ({ ...b, days }))}
      onChanged={() => setBacksTry((n) => n + 1)}
    />
  );
  // the open call: its header, what it booked, the booking form, its cards and its pinned actions
  const detail = (
    <>
      {callPending && (
        // L-sheet: the pop's place and the call's frame at once; the words arrive together
        <div className={callPhase === 'wait' ? 'ldwait' : ''} aria-busy={callPhase !== 'error'}>
          <div className="stone now-ph" aria-hidden="true">
            {bar('w40')}
            {bar('w60')}
          </div>
          <div className="grid2">
            <section className="stone card cust-ph">
              {callPhase !== 'error' && (
                <>
                  {bar('w60 head')}
                  {bar('w40')}
                  {bar('w40')}
                </>
              )}
            </section>
            <section className="stone card">
              <h2 className="h4">This call</h2>
              <dl className="kv">
                {['From', 'Line', 'Started', 'Outcome'].map((k) => (
                  <div key={k} className="kv-ph">
                    <dt>{k}</dt>
                    <dd>{callPhase !== 'error' && bar('w40')}</dd>
                  </div>
                ))}
              </dl>
              <label className="f">
                Call notes (saved on the call)
                <span className="field area ph-field" aria-hidden="true" />
              </label>
              <div className="row">
                <button className="btn primary" disabled>
                  Book a job
                </button>
                <button className="btn" disabled>
                  Not booked
                </button>
              </div>
              {/* the call's one-line note is the slot: "Getting the call…" takes its place at 3 s */}
              <LoadLine
                phase={callPhase}
                what="the call"
                note={CALL_NOTE}
                onRetry={() => setCallTry((n) => n + 1)}
              />
            </section>
          </div>
        </div>
      )}
      {!callPending && call && status && !bp && (
        // the call's header (BL-099): the ringing pop folded into one line above its cards
        <div
          className={`stone now call-head${call.status === 'ringing' ? ' ring' : ''}`}
          role={call.status === 'ringing' ? 'alert' : 'status'}
        >
          <span className={`chip ${status.chip}`}>{status.label}</span> <b>{who}</b>{' '}
          <span className="ink2">
            {phoneText(call.from)} → {phoneText(call.to)} · {clockOf(call.startedAt)} ·{' '}
            {customer
              ? 'Matched to a customer'
              : call.matches.length > 1
                ? 'Pick the customer'
                : 'No customer has this number'}
          </span>
        </div>
      )}

      {!callPending && call && bookedHere && !bp && (
        <p className="notice ok" role="status">
          <b>Booked: job #{bookedHere.jobNumber}.</b> It’s on the dispatch board
          {bookedHere.texted ? ', and the confirmation text is on its way' : ''}.{' '}
          <button
            type="button"
            className={bp ? 'ib-link' : 'btn quiet sm'}
            onClick={() => openJob(bookedHere.jobId)}
          >
            Open the job
          </button>
        </p>
      )}

      {!callPending && call && bookingHere && (
        // the Book screen's form, beside the queue (BL-101): its rules are the Book screen's
        <section className="call-book" ref={bookingRef} aria-label="Book a job for this call">
          <div className="row between">
            <h2 className="h3">Book a job</h2>
            <button type="button" className="btn quiet" onClick={() => setBooking(null)}>
              Back to the call
            </button>
          </div>
          <BookForm
            nav={nav}
            start={bookingHere}
            timeZone={zone}
            onBoard={onBoard}
            onBooked={(b) => {
              setBooking(null);
              setJustBooked({ callId: call.id, b });
              void loadCall();
            }}
          />
        </section>
      )}

      {!callPending && call && !bookingHere && (
        <div className="grid2">
          {customer ? (
            <CustomerCard
              c={customer}
              jobs={jobs}
              timeZone={zone}
              glance={
                glance && {
                  g: glance.g,
                  owingCents: owing?.key === glance.key ? owing.cents : undefined,
                }
              }
              // payments.record (and its pay link) is the office's (F-40)
              payment={
                !mirroring && held.includes('payments.record') ? (
                  <CallPayment
                    customer={customer}
                    timeZone={nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
                    tick={moneyTick}
                  />
                ) : undefined
              }
            />
          ) : call.matches.length > 1 ? (
            <section className="stone card">
              <h2 className="h3">Who is calling?</h2>
              <p className="ink2">Several customers have {phoneText(call.from)}.</p>
              {mirroring && <p className="muted small">{MIRROR_LINE}</p>}
              {bp && !mirroring ? (
                // Blueprint: the customers as rows to pick from, one action each
                <ul className="calls">
                  {call.matches.map((m) => (
                    <li key={m.customerId + m.contactName}>
                      <button
                        className="row-btn"
                        onClick={() => void update({ customerId: m.customerId }).then(loadCall)}
                      >
                        <span className="ib-crow-who">
                          <b>{m.customerName}</b>
                          <span className="ib-sub">{m.contactName}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                call.matches.map((m) =>
                  mirroring ? (
                    <p key={m.customerId + m.contactName} className="small">
                      {m.contactName} · {m.customerName}
                    </p>
                  ) : (
                    <button
                      key={m.customerId + m.contactName}
                      className="btn soft wide pick"
                      onClick={() => void update({ customerId: m.customerId }).then(loadCall)}
                    >
                      {m.contactName} · {m.customerName}
                    </button>
                  ),
                )
              )}
            </section>
          ) : mirroring ? (
            <section className="stone card">
              <h2 className="h3">No customer has {phoneText(call.from)}</h2>
              <p className="muted small">{MIRROR_LINE}</p>
            </section>
          ) : (
            <NewCustomer
              call={call}
              onCreated={(id) => {
                startBooking({ customerId: id, callId: call.id, notes: notes || null });
                void loadCall();
              }}
            />
          )}

          <section className="stone card">
            <h2 className="h4">This call</h2>
            <dl className="kv">
              <dt>From</dt>
              <dd>{phoneText(call.from)}</dd>
              <dt>Line</dt>
              <dd>{lineWord(phoneText(call.to), call.trackingLabel)}</dd>
              {/* where it came from (call-handling item 3, BL-008) */}
              <dt>Source</dt>
              <dd>
                {call.campaign ? campaignWord(call.campaign) : <span className="muted">No campaign</span>}
              </dd>
              <dt>Started</dt>
              <dd>{clockOf(call.startedAt)}</dd>
              {callJob && call.jobId && (
                <>
                  <dt>Job</dt>
                  <dd>
                    <button
                      type="button"
                      className="btn quiet sm call-job"
                      onClick={() => openJob(call.jobId!)}
                    >
                      #{callJob}
                    </button>
                  </dd>
                </>
              )}
              {call.textedBackAt && (
                <>
                  <dt>Texted back</dt>
                  <dd>{clockOf(call.textedBackAt)}</dd>
                </>
              )}
            </dl>
            {/* the ad click it came from, by the website's tag or the AdTrack import (BL-092) */}
            <AdClickOf of={{ callId: call.id }} timeZone={zone} />
            {/* booked or why not, who took it, a review (BL-003) */}
            <CallReasonPart
              call={call}
              held={held}
              onCall={setCall}
              toast={setToast}
              pickSignal={pickSignal}
              pinned
            />
            {/* the Twilio recording and what was said: owner and managers (BL-083, AD-9) */}
            {everyone && <CallTranscript key={call.id} callId={call.id} known={call.recording} />}
            <label className="f">
              {records ? 'Call notes (saved on the call)' : 'Call notes'}
              <textarea
                className="field area"
                value={notes}
                readOnly={!records}
                placeholder={records ? 'What did they say?' : undefined}
                onChange={(e) => typeNotes(call.id, e.target.value)}
                onBlur={() => void saveNotes(call.id)}
              />
            </label>
            <p className="muted small">{CALL_NOTE}</p>
          </section>
        </div>
      )}

      {!callPending && call && !bookingHere && (
        // the call's main actions, pinned at the foot of the screen (BL-099): in view at any height
        <div className="call-actions" role="group" aria-label="This call’s actions">
          {customer && !mirroring && (
            <button
              className="btn primary"
              onClick={() =>
                // the call's address and its notes, as typed (calls item 5)
                startBooking({
                  customerId: customer.id,
                  callId: call.id,
                  locationId: call.locationId,
                  notes: notes || null,
                })
              }
            >
              Book a job
            </button>
          )}
          {records && call.outcome !== 'booked' && !(call.reviewed && !everyone) && (
            <button
              type="button"
              className={bp ? 'ib-link' : 'btn'}
              onClick={() => setPickSignal((n) => n + 1)}
            >
              {call.reason ? 'Change the reason…' : 'Not booked…'}
            </button>
          )}
          {records && !call.takenBy && call.outcome !== 'booked' && (
            <button
              type="button"
              className={bp ? 'ib-link' : 'btn soft'}
              onClick={() => void update({ claim: true }, 'It’s yours to call back.')}
            >
              I’ll call them back
            </button>
          )}
          <button type="button" className={bp ? 'ib-link' : 'btn quiet'} onClick={() => onOpenCall(null)}>
            Close
          </button>
        </div>
      )}
    </>
  );

  // Blueprint: what the call booked, said once over the page's columns
  const bookedNotice = !callPending && call && bookedHere && (
    <p className="notice ok" role="status">
      <b>Booked: job #{bookedHere.jobNumber}.</b> It’s on the dispatch board
      {bookedHere.texted ? ', and the confirmation text is on its way' : ''}.{' '}
      <button type="button" className="ib-link" onClick={() => openJob(bookedHere.jobId)}>
        Open the job
      </button>
    </p>
  );
  // Blueprint: under the caller's name, how the call stands (no phone in the app: it's answered on one)
  const stateLine =
    !callPending && call
      ? `${
          call.status === 'ringing'
            ? 'Ringing'
            : call.status === 'in_progress'
              ? call.answeredBy
                ? `${call.answeredBy} answered`
                : 'Answered on a phone'
              : (status?.label ?? call.status)
        }. ${phoneText(call.from)} to ${phoneText(call.to)}, ${clockOf(call.startedAt).replace(/\.$/, '')}. ${
          customer
            ? 'Matched to a customer.'
            : call.matches.length > 1
              ? 'Pick the customer.'
              : 'No customer has this number.'
        }`
      : null;

  if (bp && callId)
    // Blueprint: an open call is its own page (CallPage), titled with who it is; the crumb goes back to
    // Calls. The call's parts on the left, its live transcript beside them.
    return (
      <div className="app calls-bp-call">
        <main className="calls-page bp-ib">
          <CallPage
            crumb={
              <>
                Inbox › <Link onPress={() => onOpenCall(null)}>Calls</Link>
              </>
            }
            title={call && call.id === callId && who ? who.replace(' · ', ', ') : 'A call'}
            {...(stateLine ? { sub: stateLine } : {})}
            {...(bookedNotice ? { notice: bookedNotice } : {})}
            main={
              <div className="ib-call calls-bp-main">
                {detail}
                {everyone && <RecordingsToMatch whenOf={whenOf} toast={setToast} />}
              </div>
            }
            side={call && !callPending ? <CallLiveTranscript call={call} /> : null}
            sideLabel="Transcript"
            settle={!!call && settle === call.id}
          />
        </main>
        {toast && (
          <div className="toast" role="status">
            {toast}
          </div>
        )}
      </div>
    );

  return (
    <div className="app">
      {/* no lede, and the parts and the booking rate beside the title (BL-099): the call's own header
          says what's ringing and who */}
      {bp ? (
        // Blueprint (BL-132): Calls and Texts and email are the Inbox's two tabs, each at its own
        // address; the booking rate is a part of Calls, opened from beside the week's rate
        <Header
          nav={nav}
          title="Calls"
          toolbar
          {...(day && part === 'calls' ? { lede: daySummary(day.total, day.booked) } : {})}
        >
          <TalkTabs nav={nav} callbacks={backs.calls?.length ?? null} />
          <div className="calls-myrate">
            <MyRate today={today} everyone={everyone} version={rateVersion} />
          </div>
          <button
            type="button"
            className="ib-link"
            onClick={() => setPart((p) => (p === 'rate' ? 'calls' : 'rate'))}
          >
            {part === 'rate' ? 'Back to the calls' : 'Booking rate'}
          </button>
        </Header>
      ) : (
        <Header nav={nav} title="Calls" toolbar>
          <div className="tabs calls-tabs" role="tablist" aria-label="Calls">
            {(
              [
                ['calls', 'Calls'],
                ['rate', 'Booking rate'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={part === k}
                className={part === k ? 'on' : ''}
                onClick={() => setPart(k)}
              >
                {label}
                {k === 'calls' && !!backs.calls?.length && (
                  <span className="count" aria-label={countLabel(backs.calls.length)}>
                    {backs.calls.length}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="calls-myrate">
            <MyRate today={today} everyone={everyone} version={rateVersion} />
          </div>
        </Header>
      )}
      <main className={`page calls-page${bp ? ' bp-ib' : ''}`}>
        {part === 'rate' && <BookingRate today={today} everyone={everyone} />}
        {/* Blueprint: the calls as the call components' lists (Ringing is the strip's) */}
        {part === 'calls' && bp && (
          <CallsLists
            today={recent}
            loading={
              <LoadLine phase={recentPhase} what="today’s calls" onRetry={() => setRecentTry((n) => n + 1)} />
            }
            backs={backs}
            whenOf={whenOf}
            canClaim={held.includes('customers.edit')}
            onOpen={(id) => onOpenCall(id)}
            onOlder={(days) => setBacks((b) => ({ ...b, days }))}
            onBacksChanged={() => setBacksTry((n) => n + 1)}
            {...(everyone ? { side: <RecordingsToMatch whenOf={whenOf} toast={setToast} /> } : {})}
          />
        )}
        {part === 'calls' && !bp && (
          <div className="calls-layout">
            <aside className="calls-queue" aria-label="Call queue">
              {ringingCard}
              {callbacks}
              {recentCard}
            </aside>
            <div className="calls-main">
              {detail}
              {!callId && (
                <section className="stone card calls-empty">
                  <p className="muted">
                    Pick a call from the queue, or wait for the phone: the caller shows up here before you
                    answer.
                  </p>
                </section>
              )}
              {everyone && <RecordingsToMatch whenOf={whenOf} toast={setToast} />}
            </div>
          </div>
        )}
      </main>
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}
